import { Box, IconButton, Typography, ButtonBase } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  format,
} from 'date-fns';
import csLocale from 'date-fns/locale/cs';

const DAY_LABELS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

const formatMonthLabel = (date) => {
  const label = format(date, 'LLLL yyyy', { locale: csLocale });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getDayInfo = (dateKey, availabilities, offeredAllocations, openAllocations) => {
  const items = availabilities[dateKey] || [];
  const offered = offeredAllocations[dateKey] || [];
  const open = openAllocations[dateKey] || [];

  return {
    hasShift: items.some((item) => item?.shiftAllocationId?._id || item?.isAssigned),
    hasAvailability: items.some((item) => !item?.isAssigned && !item?.shiftAllocationId?._id),
    hasOffered: offered.length > 0,
    hasOpen: open.length > 0,
  };
};

/**
 * When indicatorDateKeys is set (e.g. Plan only loaded one week), days outside that
 * set must not use map absence as "no openings" — return empty indicators instead.
 */
const resolveDayIndicators = ({
  dateKey,
  availabilities,
  offeredAllocations,
  openAllocations,
  indicatorDateKeys,
}) => {
  if (indicatorDateKeys && !indicatorDateKeys.has(dateKey)) {
    return {
      hasShift: false,
      hasAvailability: false,
      hasOffered: false,
      hasOpen: false,
      indicatorsKnown: false,
    };
  }

  return {
    ...getDayInfo(dateKey, availabilities, offeredAllocations, openAllocations),
    indicatorsKnown: true,
  };
};

const getPrimaryDayState = (info) => {
  if (info.hasShift) return 'shift';
  if (info.hasOffered) return 'offered';
  if (info.hasOpen) return 'open';
  if (info.hasAvailability) return 'availability';
  return 'empty';
};

const getDayStyles = ({ dayState, isSelected, today, indicatorsKnown }) => {
  const base = {
    width: 36,
    height: 36,
    fontSize: '0.875rem',
    borderRadius: '50%',
    flexShrink: 0,
    position: 'relative',
    ...(indicatorsKnown === false && { opacity: 0.4 }),
  };

  if (dayState === 'shift') {
    return {
      ...base,
      fontWeight: 700,
      color: 'var(--ink-on-accent)',
      background: 'linear-gradient(135deg, #5c6bc0 0%, #3f51b5 45%, #303f9f 100%)',
      border: '2px solid transparent',
      boxShadow: '0 4px 12px rgb(63 81 181 / 0.3)',
      '&:hover': { filter: 'brightness(1.05)' },
    };
  }

  if (dayState === 'offered') {
    return {
      ...base,
      fontWeight: isSelected ? 700 : 600,
      color: 'var(--ink)',
      background: isSelected ? 'rgba(255, 152, 0, 0.25)' : 'rgba(255, 152, 0, 0.12)',
      border: '2px solid rgba(255, 152, 0, 0.55)',
      boxShadow: 'none',
      '&:hover': { background: 'rgba(255, 152, 0, 0.2)' },
    };
  }

  if (dayState === 'open') {
    return {
      ...base,
      fontWeight: isSelected ? 700 : 600,
      color: 'var(--ink)',
      background: isSelected ? 'rgba(76, 175, 80, 0.22)' : 'rgba(76, 175, 80, 0.1)',
      border: '2px dashed rgba(76, 175, 80, 0.55)',
      boxShadow: 'none',
      '&:hover': { background: 'rgba(76, 175, 80, 0.18)' },
    };
  }

  if (dayState === 'availability') {
    return {
      ...base,
      fontWeight: isSelected ? 600 : 500,
      color: 'var(--ink-2)',
      background: isSelected ? 'rgb(255 255 255 / 0.08)' : 'transparent',
      border: '2.5px solid #3f51b5',
      boxShadow: 'none',
      '&:hover': { background: 'rgb(63 81 181 / 0.08)' },
    };
  }

  if (isSelected) {
    return {
      ...base,
      fontWeight: 600,
      color: 'var(--ink)',
      background: 'rgb(255 255 255 / 0.12)',
      border: '1px solid rgb(255 255 255 / 0.14)',
      boxShadow: 'none',
      '&:hover': { background: 'rgb(255 255 255 / 0.16)' },
    };
  }

  return {
    ...base,
    fontWeight: today ? 600 : 500,
    color: 'var(--ink-2)',
    background: 'transparent',
    border: today ? '1px solid rgb(255 255 255 / 0.12)' : '1px solid transparent',
    boxShadow: 'none',
    '&:hover': {
      background: 'rgb(255 255 255 / 0.06)',
      borderColor: 'rgb(255 255 255 / 0.16)',
    },
  };
};

const buildMonthWeeks = (monthDate) => {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weeks = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }

  return weeks;
};

function DayIndicatorDots({ info, primaryState }) {
  const dots = [];
  if (info.hasOffered && primaryState !== 'offered') {
    dots.push({ key: 'offered', color: '#ff9800' });
  }
  if (info.hasOpen && primaryState !== 'open') {
    dots.push({ key: 'open', color: '#4caf50' });
  }
  if (info.hasAvailability && primaryState !== 'availability' && primaryState !== 'empty') {
    dots.push({ key: 'availability', color: '#3f51b5' });
  }

  if (dots.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 2,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '2px',
      }}
    >
      {dots.map((dot) => (
        <Box
          key={dot.key}
          sx={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor: dot.color,
          }}
        />
      ))}
    </Box>
  );
}

function CalendarDayButton({
  day,
  availabilities,
  offeredAllocations,
  openAllocations,
  indicatorDateKeys,
  selectedDay,
  onSelectDay,
}) {
  const dateKey = format(day, 'yyyy-MM-dd');
  const info = resolveDayIndicators({
    dateKey,
    availabilities,
    offeredAllocations,
    openAllocations,
    indicatorDateKeys,
  });
  const primaryState = getPrimaryDayState(info);
  const isSelected = selectedDay && isSameDay(day, selectedDay);
  const isToday = isSameDay(day, new Date());

  return (
    <ButtonBase
      onClick={() => onSelectDay(day)}
      aria-label={format(day, 'd. MMMM yyyy', { locale: csLocale })}
      aria-current={isSelected ? 'date' : undefined}
      sx={getDayStyles({
        dayState: primaryState,
        isSelected,
        today: isToday,
        indicatorsKnown: info.indicatorsKnown,
      })}
    >
      {format(day, 'd')}
      {info.indicatorsKnown ? <DayIndicatorDots info={info} primaryState={primaryState} /> : null}
    </ButtonBase>
  );
}

const MobileMonthCalendar = ({
  visibleMonth,
  selectedDay,
  availabilities,
  offeredAllocations = {},
  openAllocations = {},
  indicatorDateKeys,
  onSelectDay,
  onPreviousMonth,
  onNextMonth,
}) => {
  const weeks = buildMonthWeeks(visibleMonth);

  return (
    <Box className="glass-surface-accent glass-month-calendar">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
          px: 0.25,
        }}
      >
        <IconButton
          onClick={onPreviousMonth}
          size="small"
          aria-label="Předchozí měsíc"
          sx={{ color: 'var(--ink-2)' }}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--ink)' }}>
          {formatMonthLabel(visibleMonth)}
        </Typography>
        <IconButton
          onClick={onNextMonth}
          size="small"
          aria-label="Následující měsíc"
          sx={{ color: 'var(--ink-2)' }}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>

      <Box className="glass-month-calendar-grid">
        <Box className="glass-month-calendar-row" role="row">
          {DAY_LABELS.map((label) => (
            <Box
              key={label}
              className="glass-month-calendar-cell glass-month-calendar-weekday"
              role="columnheader"
            >
              {label}
            </Box>
          ))}
        </Box>

        {weeks.map((week) => (
          <Box key={format(week[0], 'yyyy-MM-dd')} className="glass-month-calendar-row" role="row">
            {week.map((day) => {
              const inCurrentMonth = isSameMonth(day, visibleMonth);

              return (
                <Box
                  key={format(day, 'yyyy-MM-dd')}
                  className="glass-month-calendar-cell"
                  role="gridcell"
                  aria-hidden={!inCurrentMonth ? 'true' : undefined}
                >
                  {inCurrentMonth ? (
                    <CalendarDayButton
                      day={day}
                      availabilities={availabilities}
                      offeredAllocations={offeredAllocations}
                      openAllocations={openAllocations}
                      indicatorDateKeys={indicatorDateKeys}
                      selectedDay={selectedDay}
                      onSelectDay={onSelectDay}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { resolveDayIndicators };
export default MobileMonthCalendar;
